/**
 * TruckTooltip Component
 * Displays truck information tooltip with mobile-optimized positioning
 * Features: Boundary detection, responsive sizing, safe positioning
 */

import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import type { TooltipData } from '../types';

interface TruckTooltipProps {
  tooltip: TooltipData;
}

export const TruckTooltip: React.FC<TruckTooltipProps> = ({ tooltip }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: tooltip.x, y: tooltip.y });

  useEffect(() => {
    if (!tooltip.visible || !tooltipRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = tooltip.x + 10; // Default offset
    let y = tooltip.y - 10;

    // Horizontal boundary detection
    if (x + tooltipRect.width > viewportWidth - 10) {
      // Tooltip would overflow right edge, position to the left of cursor
      x = tooltip.x - tooltipRect.width - 10;
    }

    // Vertical boundary detection
    if (y + tooltipRect.height > viewportHeight - 10) {
      // Tooltip would overflow bottom, position above cursor
      y = tooltip.y - tooltipRect.height - 10;
    }

    // Ensure tooltip doesn't go off left edge
    if (x < 10) {
      x = 10;
    }

    // Ensure tooltip doesn't go off top edge
    if (y < 10) {
      y = 10;
    }

    setPosition({ x, y });
  }, [tooltip.x, tooltip.y, tooltip.visible]);

  if (!tooltip.visible) return null;

  return (
    <Box
      ref={tooltipRef}
      sx={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        backgroundColor: '#1f2937',
        color: '#ffffff',
        padding: { xs: '6px 10px', sm: '8px 12px' },
        borderRadius: '6px',
        fontSize: { xs: '0.6875rem', sm: '0.75rem' },
        pointerEvents: 'none',
        zIndex: 1000,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        minWidth: { xs: 150, sm: 200 },
        maxWidth: { xs: 250, sm: 300 },
        transition: 'left 0.1s ease, top 0.1s ease',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          mb: 0.5,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {tooltip.truckName}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
          opacity: 0.9,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        VIN: {tooltip.vin}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
          opacity: 0.9,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        License: {tooltip.licensePlate}
      </Typography>
    </Box>
  );
};
